import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CustomCalendar from "../../components/elements/custom-calendar";
import { Button, Input, notification } from "antd";
import { Dayjs } from "dayjs";
import moment from "moment";
import DataProvider from "../../services";
import LoadWrapper from "../../components/elements/load-wrapper";
import { schedulePassed } from "../../utils";

const { TextArea } = Input;
type NotificationType = "success" | "info" | "warning" | "error";

export default function Reschedule() {
  const navigate = useNavigate();
  const params = useParams();
  const [api, contextHolder] = notification.useNotification();

  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [schedule, setSchedule] = useState<Dayjs | null>(null);
  const [time, setTime] = useState<any>(null);
  const [timeFrames, setTimeFrames] = useState<any[]>([]);
  const [rescheduleReason, setRescheduleReason] = useState<string>("");

  const openNotificationWithIcon = (
    type: NotificationType,
    title: string,
    message: string
  ) => {
    api[type]({ message: title, description: message });
  };

  // Same source of available slots the initial booking flow uses
  // (select-schedule.tsx) - rescheduling picks a new slot the same way.
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const results = await DataProvider.getAvailableSchedule();
      setData(results.data);
    } catch (error) {
      openNotificationWithIcon(
        "error",
        "Could not load availability",
        "Please refresh and try again."
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!schedule || data.length === 0) {
      setTimeFrames([]);
      return;
    }
    setTime(null);
    const matching = data.filter(
      (value) =>
        moment(schedule.toDate()).format("DD MM YYYY") ===
        moment(value.date, "DD-MM-YYYY").format("DD MM YYYY")
    );
    let arr: any[] = [];
    for (const value of matching) {
      // Tag each timeframe with the schedule it actually belongs to, so
      // submitting always uses the correct scheduleId - not just the
      // first schedule entry for the day (see select-schedule.tsx).
      value.timeFrames.forEach((val: any) =>
        arr.push({ ...val, _scheduleId: value.id })
      );
    }
    arr.sort((a, b) =>
      moment(a.startTime, "HH:mm").diff(moment(b.startTime, "HH:mm"))
    );
    setTimeFrames(arr);
  }, [schedule, data]);

  async function _onProceed() {
    if (!time || !time._scheduleId) {
      return openNotificationWithIcon(
        "warning",
        "Schedule",
        "Kindly select a date and time!"
      );
    }
    if (!params.id) return;
    setSubmitting(true);
    try {
      await DataProvider.rescheduleAppointment(params.id, {
        scheduleId: time._scheduleId,
        timeFrameId: time.id,
        rescheduleReason: rescheduleReason.trim() || undefined,
      });
      openNotificationWithIcon(
        "success",
        "Appointment rescheduled",
        "Your appointment has been moved to the new time."
      );
      navigate(`/main/appointments/${params.id}`);
    } catch (error: any) {
      setSubmitting(false);
      openNotificationWithIcon(
        "error",
        "Could not reschedule",
        error?.response?.data?.message ||
          "That slot may no longer be available. Please pick another."
      );
    }
  }

  return (
    <div className="pt-3">
      <div className="bg-white border border-solid border-gray-200 rounded-lg w-full lg:h-[85vh] min-h-[85vh] flex justify-center py-3">
        <LoadWrapper loading={loading}>
          <div className="flex justify-center">
            <div className="md:w-[50%] w-[90%]">
              <h3 className="font-semibold text-sm text-gray-600 my-2">
                Set a new schedule
              </h3>
              <div className="">
                <CustomCalendar setSchedule={setSchedule} />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-gray-600 mt-4 mb-2">
                  Select a time
                </h3>
                <div className="flex justify-start w-full flex-wrap">
                  {timeFrames.length > 0 ? (
                    timeFrames
                      .filter((value: any) => !value.booked)
                      .map((i: any, idx: number) => {
                        const passed = schedulePassed(
                          moment(schedule?.toDate()).format("DD-MM-YYYY"),
                          i.startTime
                        );
                        return !passed ? (
                          <span
                            key={idx}
                            onClick={() => setTime(i)}
                            className={`${
                              i === time
                                ? "border-2 border-solid border-blue-800 bg-[#F0F9FF] text-[#06509E]"
                                : "border border-solid border-gray-300"
                            } p-2 m-2 rounded-md cursor-pointer`}
                          >
                            {i.startTime} - {i.endTime}
                          </span>
                        ) : null;
                      })
                  ) : (
                    <p className="my-5">
                      {schedule
                        ? `No available slots on ${moment(
                            schedule.toDate()
                          ).format("Do MMMM YYYY")}`
                        : "Pick a date to see available times."}
                    </p>
                  )}
                </div>

                <TextArea
                  rows={3}
                  placeholder="Reason for rescheduling (optional)"
                  value={rescheduleReason}
                  onChange={(e) => setRescheduleReason(e.target.value)}
                  className="mt-2"
                />

                <div className="my-5">
                  <Button
                    onClick={_onProceed}
                    type="primary"
                    size="large"
                    loading={submitting}
                    disabled={submitting}
                    color="#0081D4"
                    htmlType="submit"
                    className="bg-[#0081D4] w-full"
                  >
                    Save
                  </Button>

                  <Button
                    size="large"
                    onClick={() => navigate(-1)}
                    className="w-full my-3"
                  >
                    Back
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </LoadWrapper>
      </div>
      {contextHolder}
    </div>
  );
}
